import { type FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form-latest';
import { _isNotBlank, _notNil } from '@/littledash';
import Button from '@/components/UI/Button/Button';
import type { FieldConfig } from '../VerticalStepForm.model';
import { resolveField } from '../Fields/FieldRegistry';
import styles from './StepContent.module.scss';

interface StepContentProps {
  stepId: string;
  fields: Array<FieldConfig>;
  description?: string;
  required: boolean;
  onAdvance: (stepId: string) => void;
  onSkip: (stepId: string) => void;
  onValueChange: (stepId: string) => void;
}

/** Expanded step body. Resolves and renders fields from config, with Next and Skip action buttons. */
const StepContent: FC<StepContentProps> = ({
  stepId,
  fields,
  description,
  required,
  onAdvance,
  onSkip,
  onValueChange,
}) => {
  const { control } = useFormContext();
  const fieldNames = useMemo(() => fields.map((f) => f.name), [fields]);
  const watchedValues = useWatch({ control, name: fieldNames });
  const hasData = watchedValues.some((value) => {
    if (_notNil(value) && typeof value === 'object') {
      return _isNotBlank(String(value.value ?? ''));
    }
    return _isNotBlank(String(value ?? ''));
  });

  const handleAdvance = useCallback(() => onAdvance(stepId), [onAdvance, stepId]);
  const handleSkip = useCallback(() => onSkip(stepId), [onSkip, stepId]);
  const handleValueChange = useCallback(() => onValueChange(stepId), [onValueChange, stepId]);

  return (
    <div
      className={styles.content}
      data-test-component="StepContent"
      data-test-element="container"
      data-testid="step-content"
    >
      {description && <p className={styles.description}>{description}</p>}
      <div className={styles.fields}>
        {fields.map((fieldConfig) => {
          const FieldComponent = resolveField(fieldConfig.type);
          return (
            <FieldComponent
              key={fieldConfig.name}
              name={fieldConfig.name}
              label={fieldConfig.label}
              showLabel={fieldConfig.showLabel}
              placeholder={fieldConfig.placeholder}
              loadOptions={fieldConfig.loadOptions}
              rules={fieldConfig.rules}
              onValueChange={handleValueChange}
            />
          );
        })}
      </div>
      <div className={styles.actions}>
        <Button primary disabled={!hasData} onClick={handleAdvance} testId="step-content__next">
          Next
        </Button>
        {!required && (
          <Button plain onClick={handleSkip} testId="step-content__skip">
            Skip
          </Button>
        )}
      </div>
    </div>
  );
};

export default StepContent;
